import { handleAction, combineActions } from 'redux-actions';
import { group } from 'd3-array';
import { timeFormat } from 'd3-time-format';

import { clearAllDataAction, loadMockDataAction } from '../actions/data';
import { dropboxFetchAction } from '../actions/dropbox';
import { fhirFetchAction } from '../actions/fhir';
import { fitFetchAction } from '../actions/google';
import { nightscoutFetchAction } from '../actions/nightscout';
import { omakantaFetchAction } from '../actions/omakanta';
import { ouraFetchAction } from '../actions/oura';
import { wellmoFetchAction } from '../actions/wellmo';
import { logoutAction } from '../actions/authentication';
import { defaultErrorHandler, isError } from './errors';

export const dayFormat = timeFormat('%Y-%m-%d');
export const monthFormat = timeFormat('%Y-%m');
export const yearFormat = timeFormat('%Y');
export const dayKey = d => (dayFormat(new Date(d.time)));
const monthKey = d => (monthFormat(new Date(d.time)));
const yearKey = d => (yearFormat(new Date(d.time)));
export const idKey = d => d.id || d.time;
export const ascendingValueSort = (a, b) => a.value === b.value ? 0 : (a.value < b.value ? -1 : 1);
export const sortAscendingTime = (array) => {

  const mapped = array.map((e, i) => {
    if (!e.time) {
      // console.error('No time!', e);
      return undefined;
    }
    return { index: i, value: e.time.toISOString ? e.time.toISOString() : e.time };
  });
  mapped.sort(ascendingValueSort);
  return mapped.map(e => array[e.index]);
};
export const filterValuesForDate = values => date => (values && values[dayFormat(date)]) || [];
export const filterValuesForYear = values => date => (values && values[yearFormat(date)]) || [];

const nestData = (data, type, key) => {
  const typeData = data[type];
  if (!typeData || !typeData.length) {
    return null;
  }
  return group(typeData, key);
};

const deduplicate = (oldValues = [], newValues = []) => {
  if (!oldValues.length && !newValues.length) {
    return null;
  }
  if (!oldValues.length) {
    return sortAscendingTime(newValues);
  }
  if (!newValues.length) {
    return oldValues;
  }
  const combined = [...oldValues, ...newValues];
  const map = new Map(combined.map(v => [idKey(v), v]));
  const values = Array.from(map.values()).filter((v, i, a) => {
    if (!v) {
      console.log('Rejecting value', { v, a, i });
    }
    return !!v;
  });
  return sortAscendingTime(values);
};

const addToState = ({ state, type, nest }) => {
  if (!nest || !nest.keys) {
    return state;
  }
  const newState = {
    ...state,
    [type]: { ...state[type] },
  };
  const keys = Array.from(nest.keys()).sort();
  keys.forEach((k) => {
    const oldValues = state[type] && state[type][k];
    let newValues = nest.get(k);
    if (type === 'notes') {
      // Filter duplicates, AAPS creates these...
      newValues = sortAscendingTime(Object.values(newValues.reduce((o, v) => {
        o[v.id || v.time] = v;
        return o;
      }, {})));
    }
    newState[type][k] = deduplicate(oldValues, newValues);
    if (type === 'basal') {
      newValues.forEach((d) => {
        if (!newState.stats.maxBasalRate || (state.stats.maxBasalRate < d.rate)) {
          newState.stats = {
            ...newState.stats,
            maxBasalRate: d.rate,
          };
        }
      });
    }
  });
  return newState;
};

export const initialState = Object.freeze({
  basal: {},
  bloodPressure: {},
  bolus: {},
  exercises: {},
  hba1c: {},
  hr: {},
  longActing: {},
  notes: {},
  nutrition: {},
  photos: {},
  places: {},
  pumpEvents: {},
  sgv: {},
  sleep: {},
  smbg: {},
  steps: {},
  stats: {},
  transports: {},
  weight: {},
});

export const dataReducer = (state, action) => {
  Object.freeze(state);
  if (action.type === logoutAction.toString() || action.type === clearAllDataAction.toString()) {
    // clean all data!
    return initialState;
  }
  if (isError(action)) {
    return state;
  }

  let nextState = { ...state };
  // Nest most data by day
  // Somewhat easy to combine monthly averages, when needed.
  [
    'basal', 'bolus', 'exercises', 'hr', 'longActing', 'notes', 'nutrition', 'photos', 'places',
    'pumpEvents', 'sgv', 'sleep', 'smbg', 'steps', 'transports',
  ].forEach((type) => {
    nextState = addToState({
      state: nextState,
      type,
      nest: nestData(action.payload, type, dayKey),
    });
  });
  // Nest parameters that are measured around once per day or week by month
  [].forEach((type) => {
    nextState = addToState({
      state: nextState,
      type,
      nest: nestData(action.payload, type, monthKey),
    });
  });
  // Nest more seldomly measured parameters by year
  ['bloodPressure', 'hba1c', 'weight'].forEach((type) => {
    nextState = addToState({
      state: nextState,
      type,
      nest: nestData(action.payload, type, yearKey),
    });
  });
  const { profiles } = action.payload;
  if (profiles) {
    // console.log('Adding Nightscout profiles', profiles);
    nextState.profiles = {
      ...nextState.profiles,
      ...profiles,
    };
  }
  return nextState;
};

export default handleAction(
  combineActions(
    //  loginAction,
    clearAllDataAction,
    logoutAction,
    loadMockDataAction,
    dropboxFetchAction,
    fhirFetchAction,
    fitFetchAction,
    nightscoutFetchAction,
    omakantaFetchAction,
    ouraFetchAction,
    wellmoFetchAction,
  ),
  {
    next: dataReducer,
    throw: defaultErrorHandler,
  },
  initialState,
);
